// @ts-nocheck
import React, { useState, useRef, useEffect, useMemo, useContext } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Container, Row, Col } from 'react-bootstrap';

import { HeaderS } from '@shared/ui/header';
import { Footer } from '@shared/ui/footer';
import { changeLanguage } from '@entities/language/store';
import {
  formResetComplete,
  dcbUnsubscribe,
} from '@features/card-subscription/model/subscription-payment-flow';
import { Helpers } from '@shared/utils/Helpers';
import { setHtmlDirection } from '@shared/utils';
import { getFinalNumberFromDialCode, setTheme } from '@shared/utils/general';
import { APP_THEMES } from '@shared/config/constants/app';
import { TelInput } from '@features/phone-subscription';
import parsePhoneNumber from 'libphonenumber-js/mobile';
import { isDZ, isJO, isMA, isPT } from '@entities/country';
import { getCountryPlaceholder } from '@entities/country';
import { Banner } from '@features/phone-subscription';
import { useParams } from 'react-router-dom';
import LoaderFreshSVG from '@shared/assets/svgs/LoaderFresh';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@shared/hooks/useQuery';
import { getUnsubLanguageConfig } from '@features/dcb-unsubscribe-country/model/getUnsubLanguageConfig';
import { unsubBannerConfig } from '@shared/config/constants/Banners';
import Alert from '@shared/ui/alert/ui/Alert';
import { subscriptionAtom } from '@shared/store/slice-atoms';
import { SubscribtionContext } from '@shared/store/context/SubscribtionContext';
import {
  PT_CC_EMAIL,
  PT_CC_PHONE,
  PT_SERVICE_LOGO,
  PT_TERMS_URL,
  PT_UNSUBSCRIBE_SUCCESS_PATH,
} from '@shared/config/constants/portugal';
import { EVENT_NAMES, LogService } from '@shared/services/LogService';
import ptStyles from '@features/dcb-unsubscribe-country/styles/PortugalUnsubscribe.module.css';

const SUPPORTED_UNSUB_COUNTRIES = new Set(['ly', 'dz', 'pt']);

const isSupportedUnsubCountry = (country) =>
  SUPPORTED_UNSUB_COUNTRIES.has((country || '').toLowerCase());

const DCBUnsubscribeCountry = ({ isArabic = false }) => {
  const { t, i18n } = useTranslation();
  const formStartRef = useRef(null);
  const howItWorksRef = useRef(null);
  const history = useHistory();
  const { query } = useQuery();

  const { unsubscribeData, btnLoading, needToResetForm } = useAtomValue(subscriptionAtom);
  const { phone, setPhone } = useContext(SubscribtionContext);

  const { country } = useParams();
  const routeCountry = (country || '').toLowerCase();
  const isPTUnsub = isPT(routeCountry);
  const languageConfig = useMemo(() => getUnsubLanguageConfig(routeCountry), [routeCountry]);
  const [countryCode, setCountryCode] = useState('');

  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [genericError, setGenericError] = useState('');

  useEffect(() => {
    if (isPTUnsub) return;
    if (unsubscribeData?.otpDigitsCount > 0 && isSupportedUnsubCountry(routeCountry)) {
      history.push(`/dcb/unsub/otp?country=${routeCountry}`);
    }
  }, [unsubscribeData, routeCountry, history, isPTUnsub]);

  useEffect(() => {
    if (!routeCountry) return;

    if (isSupportedUnsubCountry(routeCountry)) {
      setGenericError('');
    } else {
      setGenericError(t('unsub_country_not_found'));
    }
  }, [routeCountry, t]);

  useEffect(() => {
    if (isArabic) {
      let lang = { locale: 'ar', displayName: 'العربية', direction: 'rtl' };
      i18n.changeLanguage(lang.locale);
      changeLanguage(lang);
      setHtmlDirection('rtl');
    } else if (isPTUnsub) {
      const lang = { locale: 'pt', displayName: 'Português', direction: 'ltr' };
      i18n.changeLanguage(lang.locale);
      changeLanguage(lang);
      setHtmlDirection('ltr');
    }

    setTheme(APP_THEMES.WHITE);
  }, [isArabic, isPTUnsub, i18n]);

  useEffect(() => {
    if (!isPTUnsub) return;

    const msisdn = query.get('msisdn') || Helpers.getUrlParameter('msisdn');
    if (msisdn && !phone) {
      setPhone(msisdn);
      setCountryCode('351');
    }
  }, [isPTUnsub]);

  useEffect(() => {
    if (needToResetForm) {
      setPhone('');
      formResetComplete();
    }
  }, [needToResetForm]);

  const handleSubmitUnsubscribe = (e) => {
    e.preventDefault();
    if (genericError) return;

    if (errors.phone || phone === '') {
      setErrorMessage(t('error_phone'));
      return;
    } else {
      setErrorMessage('');
    }
    dcbUnsubscribe(
      {
        mobile: phone,
        mobileCountryCode: countryCode,
        forceIgnoreVuz: true,
        forceIgnoreAggregator: true,
        country: routeCountry,
      },
      true
    );
  };

  const handlePhoneChange = (isValid, inputAsIs, iso2, international) => {
    let lettersRegExp = /[a-zA-Z+]/g; //:\'\".,-:;

    if (lettersRegExp.test(inputAsIs)) {
      return;
    }

    if (inputAsIs.includes('.') || inputAsIs.includes(',')) {
      return;
    }

    if (!iso2) return;
    if (iso2 && !iso2.iso2) return;
    const dialCode = iso2.dialCode;
    const isoCode = iso2.iso2.toUpperCase();

    let finalNumber = getFinalNumberFromDialCode(inputAsIs, dialCode);

    let dialCode2 = '972';

    if (dialCode === '970') {
      finalNumber = getFinalNumberFromDialCode(finalNumber, dialCode2);
    }

    const phoneNumber = parsePhoneNumber(finalNumber, isoCode);
    const isPhoneNumberValidForJO =
      Helpers.isPhoneNumberValidSpecialCases(inputAsIs) && isJO(routeCountry);
    const isPhoneNumberValidForMA =
      Helpers.isPhoneNumberValidSpecialCases(inputAsIs) && isMA(routeCountry);
    const isPhoneNumberValidForDZ =
      Helpers.isPhoneNumberValidSpecialCases(inputAsIs) && isDZ(routeCountry);

    let newFinal = '';
    setPhone(inputAsIs);
    if (finalNumber.includes(`+${dialCode}`)) {
      newFinal = finalNumber.split(`+${dialCode}`)[1];
    } else if (finalNumber.includes(`+${dialCode2}`)) {
      newFinal = finalNumber.split(`+${dialCode2}`)[1];
    } else if (isPhoneNumberValidForJO) {
      newFinal = inputAsIs;
    } else if (isPhoneNumberValidForMA) {
      newFinal = inputAsIs;
    } else {
      newFinal = finalNumber;
    }

    setErrorMessage('');
    setPhone(newFinal);
    setCountryCode(dialCode);

    if (
      (phoneNumber && phoneNumber?.isValid()) ||
      isPhoneNumberValidForJO ||
      isPhoneNumberValidForMA ||
      isPhoneNumberValidForDZ
    ) {
      let newErrors = { ...errors };
      delete newErrors.phone;
      setErrors(newErrors);
    } else {
      setErrors({ ...errors, phone: t('error_phone') });
    }
  };

  const preferredCountries = useMemo(() => {
    if (!routeCountry) return ['ae'];

    return [routeCountry];
  }, [routeCountry]);

  const inputPlaceholder = getCountryPlaceholder(preferredCountries[0]) || 'XXXXXXXXX';
  const isPTCancelled = isPTUnsub && query.get('status') === 'cancelled';

  useEffect(() => {
    if (!isPTUnsub || isPTCancelled) return;
    if (unsubscribeData && Object.keys(unsubscribeData).length > 0) {
      history.replace(PT_UNSUBSCRIBE_SUCCESS_PATH);
    }
  }, [unsubscribeData, isPTUnsub, isPTCancelled, history]);

  const handleTermsClick = () => {
    LogService.handleEvent(EVENT_NAMES.main_click_button_terms_and_conditions);
  };

  if (isPTUnsub) {
    return (
      <article className={ptStyles.page}>
        <header className={ptStyles.header}>
          <img src={PT_SERVICE_LOGO} className={ptStyles.logo} alt="VUZ VIP" />
        </header>
        <div className={ptStyles.content}>
          {isPTCancelled ? (
            <>
              <h1 className={ptStyles.successTitle}>{t('pt_unsub_success_title')}</h1>
              <p className={ptStyles.successCopy}>{t('pt_unsub_success')}</p>
            </>
          ) : (
            <form onSubmit={handleSubmitUnsubscribe}>
              <h1 className={ptStyles.title}>{t('pt_cancel_page_title')}</h1>
              <p className={ptStyles.copy}>
                {t('welcome_pt_cc_phone')}: {PT_CC_PHONE}
              </p>
              <p className={ptStyles.copy}>
                {t('welcome_pt_cc_email')}:{' '}
                <a href={`mailto:${PT_CC_EMAIL}`}>{PT_CC_EMAIL}</a>
              </p>
              <a
                className={ptStyles.terms}
                href={PT_TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleTermsClick}
              >
                {t('terms_and_conditions')}
              </a>
              {!routeCountry ? (
                <LoaderFreshSVG />
              ) : (
                <div className={ptStyles.phoneWrap}>
                  <TelInput
                    phone={phone}
                    handlePhoneChange={handlePhoneChange}
                    preferredCountries={preferredCountries}
                    showCountryDropdown={false}
                    setSelectedCountry={() => {}}
                    hideCountryFlag={false}
                    placeholder={inputPlaceholder}
                  />
                </div>
              )}
              <Alert />
              {errorMessage && <p className="error-message">{errorMessage}</p>}
              {errors.phone && phone && <p className="error-message">{errors.phone}</p>}
              {genericError && <p className="error-message">{genericError}</p>}
              <button
                id="unsubscribe-button"
                type="submit"
                className={ptStyles.cancelButton}
                disabled={btnLoading}
              >
                {btnLoading ? t('processing') : t('pt_cancel_button')}
              </button>
            </form>
          )}
        </div>
      </article>
    );
  }

  return (
    <article className="landing-article">
      <HeaderS
        formStartRef={formStartRef}
        howItWorksRef={howItWorksRef}
        languageConfig={languageConfig}
        isCC
        isUnsubPage
      />
      <Banner bannerConfig={unsubBannerConfig} />
      <Container className="container-sm">
        <section className="landing-section-4">
          <form onSubmit={handleSubmitUnsubscribe}>
            <Row className="mt-3">
              <Col>
                <p className="teleport-subtitle">{t('enter_mobile_to_unsubscribe')}</p>
              </Col>
            </Row>

            {!routeCountry ? (
              <LoaderFreshSVG />
            ) : (
              <Row className="mt-2 mb-2">
                <Col>
                  <TelInput
                    phone={phone}
                    handlePhoneChange={handlePhoneChange}
                    preferredCountries={preferredCountries}
                    showCountryDropdown={false}
                    setSelectedCountry={() => {}}
                    hideCountryFlag={false}
                    placeholder={inputPlaceholder}
                  />
                </Col>
              </Row>
            )}

            <div className="text-center mb-3">
              <Alert />
              {errorMessage && <p className="error-message">{errorMessage}</p>}
              {errors.phone && phone && <p className="error-message">{errors.phone}</p>}
              {genericError && <p className="error-message">{genericError}</p>}

              <button
                id="unsubscribe-button"
                type="submit"
                className="vuz-btn-1 large lh-07 mt-2 join-btn-confirm"
              >
                <span className="subscribe-btn-txt">
                  {btnLoading ? (
                    <>
                      <i className="fas fa-circle-notch fa-spin sm-icon-margin-1" />
                      {t('processing')}
                    </>
                  ) : (
                    t('unsubscribe')
                  )}
                </span>
              </button>
              <br />
              <br />
            </div>
          </form>
          <Footer
            isWhite
            hideFeatures
            isOTP
            vipFeaturesConfig={{ hide: true }}
            followUsConfig={{ hide: true }}
            moreAboutUsConfig={{ hide: true }}
            aboutUsConfig={{ hide: true }}
          />
        </section>
      </Container>
    </article>
  );
};

export { DCBUnsubscribeCountry };
